<template>
  <div class="main-layout" v-if="!isRedirect">
    <app-header />
    <div class="sidebar-holder">
      <aside class="sidebar" v-if="authenticated">
        <div class="sidebar-content">
          <sidebar :data="permissionControlledMenu"></sidebar>
        </div>
      </aside>
      <main class="main" :class="[authenticated, authenticated ? 'child-content-auth' : 'child-content']">
        <router-view />
      </main>
    </div>

    <div class="toast-holder text-center align-items-center justify-content-center">
      <toast-message ref="toastMessage"></toast-message>
    </div>
  </div>
  <div class="d-flex justify-content-center" v-else>
    <loader size="sm" :loading="redirect" />
  </div>
</template>

<script>
import "devextreme/dist/css/dx.light.css";
import AppHeader from "~/components/AppHeader.vue";
import { errorMessageList } from "~/helpers/common";
import { mapActions, mapGetters } from "vuex";
import { hasModuleAccess } from "~/helpers/Permissions";
import { eventBus } from "@/plugins/eventBus";
import { errorMessage } from "@/helpers/common";
import ToastMessage from "~/components/Mobile/toastMessage.vue";
import Sidebar from "~/components/Sidebar/index.vue";
import Loader from "@/components/Loader.vue";
import Cookies from "js-cookie";
import { menu } from "@/config/menu.js"; // Import the menu configuration

export default {
  name: "MainLayout",
  data() {
    return {
      showed: false,
      redirect: false,
      onToastMessageHandler: null,
    };
  },
  components: {
    Loader,
    Sidebar,
    AppHeader,
    ToastMessage,
  },
  computed: {
    ...mapGetters({
      authenticated: "auth/check",
      user: "auth/user",
      departments: "company/departments",
      domain: "auth/domain",
      generalSettings: "settings/generalSettings",
    }),
    permissionControlledMenu() {
      if (!Array.isArray(menu)) {
        return [];
      }

      const toTranslatedItem = (entry) => {
        return {
          ...entry,
          title: entry?.titleKey ? this.$t(entry.titleKey) : entry?.title || "-",
        };
      };

      const isAllowed = (entry) => {
        const needSuperAdmin = entry?.needSuperAdmin || false;
        if (needSuperAdmin && (!this.user || !this.user.is_superadmin)) {
          return false;
        }

        if (entry?.permission) {
          if (!hasModuleAccess(this.user, entry.permission)) {
            return false;
          }
        }

        if (entry?.setting) {
          const setting = this.generalSettings?.find(
            (item) => (item?.code || "").toUpperCase() === entry.setting.toUpperCase()
          );

          return [true, 1, "1", "true"].includes(setting?.value);
        }

        return true;
      };

      return menu
        .map((rawItem) => {
          const item = toTranslatedItem(rawItem);

          if (!isAllowed(item)) {
            return null;
          }

          if (Array.isArray(item.child)) {
            const child = item.child
              .map((rawSubitem) => toTranslatedItem(rawSubitem))
              .filter((subitem) => isAllowed(subitem));

            if (!child.length) {
              return null;
            }

            return {
              ...item,
              child,
            };
          }

          return item;
        })
        .filter(Boolean);
    },
    isRedirect() {
      return this.$route && this.$route.path.toString().indexOf("/redirect") > -1 && this.$route.query.fwd;
    },
  },
  methods: {
    ...mapActions({
      getDepartments: "company/getDepartments",
    }),
    async handleGlobalValidationErrors() {
      let vm = this;

      eventBus.$on("validation-errors", (errors) => {
        errorMessageList(vm, errors);
      });

      if (!this.showed) {
        eventBus.$on("session-expired", (errors) => {
          errorMessage(vm, vm.$t("token_expired_alert_title"));
          this.showed = true;
        });
      }
    },
    async getCompanyDomainUnits(company) {
      if (company && company.uuid) {
        await this.getDepartments({
          company_uuid: company.uuid,
        });
      }
    },
    async getGeneralSettings() {
      if (!this.generalSettings?.length) {
        await this.$store.dispatch("settings/getSettingsForGeneralUsage");
      }
    },
    async handleRedirect() {
      if (this.isRedirect) {
        this.$set(this, "redirect", true);
        await this.$router.push({
          path: this.$route.query.fwd,
        });
      }
    },
    showGlobalToast(response) {
      const type = response?.type ?? "error";
      const message = response?.message ?? this.$t("error_alert_text");
      const toast = this.$refs?.toastMessage;

      if (toast && typeof toast.showToastMessage === "function") {
        toast.showToastMessage(type, message);
        return;
      }

      this.$nextTick(() => {
        const delayedToast = this.$refs?.toastMessage;
        if (delayedToast && typeof delayedToast.showToastMessage === "function") {
          delayedToast.showToastMessage(type, message);
        }
      });
    },
  },
  async mounted() {
    const dom = await this.$store.dispatch("auth/checkDomain", {
      domain: Cookies.get("domain"),
    });
    await this.$store.dispatch("auth/fetchUser");

    await this.handleRedirect();
    await this.handleGlobalValidationErrors();

    await this.getCompanyDomainUnits(dom);
    await this.getGeneralSettings();

    this.onToastMessageHandler = (response) => {
      try {
        this.showGlobalToast(response);
      } catch (e) {
        console.log("toast error", e);
      }
    };
    eventBus.$on("onToastMessage", this.onToastMessageHandler);
  },
  beforeDestroy() {
    if (this.onToastMessageHandler) {
      eventBus.$off("onToastMessage", this.onToastMessageHandler);
    }
  },
  watch: {
    $route: {
      handler: function (newVal) {
        this.handleRedirect();
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
dx-license {
  display: none !important;
}

@import "@/assets/scss/global/global";

.main-layout {
  background-color: $gray-10;
}

/* Flex container under header: sidebar + main */
.sidebar-holder {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  height: calc(100vh - 64px); /* header height */
  margin-top: 8px;
  overflow: hidden; /* prevent joint scroll */
}

/* Sidebar fills height; scrolls independently */
.sidebar {
  width: max-content;
  flex: 0 0 auto;
  height: 100%;
  padding: 12px 8px 24px 8px;
  overflow-y: auto;
  overflow-x: hidden;
  background: $gray-10;
}

/* Content inside sidebar inherits intrinsic width */
.sidebar-content {
  width: max-content;
  overflow: visible !important;
}

/* Prevent wrapping to allow intrinsic width expansion */
.sidebar ::v-deep(.dx-menu-item-content),
.sidebar ::v-deep(.sidebar-item-label) {
  white-space: nowrap;
}

/* Main area scrolls separately */
.main {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  overflow: auto;
  padding: 16px 16px 16px 16px;
  box-sizing: border-box;
  background: $gray-10;
  //background: linear-gradient(135deg, #f7f7f7 0%, #e4efe8 100%);
}
</style>
